/**
 * ServiceHistoryDrawer — slide-in panel triggered from the
 * `ContractedServicesCard`. Shows the last 30 days of activity for a single
 * specialty service on a specific property. Data source:
 *   GET /api/properties/{property_id}/service-history/{service_slug}
 * Access-controlled server-side (admin, PM-of-property, or assigned staff).
 */
import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import { Calendar, CheckCircle2, Clock, User as UserIcon, Camera, FileText, Loader2 } from 'lucide-react';
import { specialtyLabel } from '../lib/specialtyServices';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  done: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  closed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  scheduled: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  in_progress: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  cancelled: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/40',
  missed: 'bg-red-500/15 text-red-300 border-red-500/40',
};

const prettyDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      // scheduled_date is YYYY-MM-DD; render as-is
      return iso;
    }
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

export default function ServiceHistoryDrawer({
  open,
  onOpenChange,
  propertyId,
  propertyName,
  serviceSlug,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !propertyId || !serviceSlug) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${API}/api/ops/properties/${propertyId}/service-history/${serviceSlug}?days=30`,
          { credentials: 'include' },
        );
        const j = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(j.detail || 'Failed to load service history');
          setData(null);
        } else {
          setData(j);
        }
      } catch (_) {
        if (!cancelled) { setError('Network error'); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, propertyId, serviceSlug]);

  const label = specialtyLabel(serviceSlug);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-zinc-950 border-l border-[#C9A962]/40 text-white overflow-y-auto"
        data-testid="service-history-drawer"
      >
        <SheetHeader className="mb-4">
          <SheetTitle
            className="text-xl"
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#C9A962',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {label} · Service History
          </SheetTitle>
          <SheetDescription className="text-[11px] text-zinc-400 mt-1">
            {propertyName || 'This property'} — last 30 days
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500" data-testid="service-history-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading history…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="service-history-error">
            {error}
          </div>
        )}

        {data && !loading && !error && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4" data-testid="service-history-summary">
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Completed</div>
                <div className="text-2xl font-bold text-emerald-400">{data.completed}</div>
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total records</div>
                <div className="text-2xl font-bold text-white">{data.total}</div>
              </div>
            </div>

            {data.items.length === 0 ? (
              <div
                className="rounded-md border border-dashed border-zinc-700 bg-zinc-900/50 p-6 text-center"
                data-testid="service-history-empty"
              >
                <FileText className="h-8 w-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-sm text-zinc-300 font-semibold">No {label.toLowerCase()} activity in the last 30 days.</p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Once the field team logs a completion, it will appear here automatically.
                </p>
              </div>
            ) : (
              <ol className="space-y-3" data-testid="service-history-list">
                {data.items.map((it, idx) => {
                  const statusKey = String(it.status || '').toLowerCase();
                  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.pending;
                  return (
                    <li
                      key={`${it.id || idx}-${it.kind}`}
                      className="relative rounded-lg border border-zinc-800 bg-zinc-900/70 p-3"
                      data-testid={`service-history-item-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] uppercase tracking-widest text-zinc-500">
                              {it.kind === 'scheduled' ? 'Scheduled' : 'Request'}
                            </span>
                            <Badge className={`text-[10px] font-bold uppercase border ${statusStyle}`}>
                              {statusKey === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                              {it.status || 'pending'}
                            </Badge>
                          </div>
                          <div className="mt-1 text-xs text-zinc-300 flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-zinc-500" />
                            {prettyDate(it.occurred_at)}
                          </div>
                          {it.assigned_to && (
                            <div className="mt-1 text-xs text-zinc-400 flex items-center gap-1">
                              <UserIcon className="h-3 w-3 text-zinc-500" />
                              {it.assigned_to}
                            </div>
                          )}
                          {it.notes && (
                            <p className="mt-2 text-xs text-zinc-300 leading-snug break-words">
                              {it.notes}
                            </p>
                          )}
                          {Array.isArray(it.photos) && it.photos.length > 0 && (
                            <div className="mt-2 flex items-center gap-1 text-[10px] text-zinc-500">
                              <Camera className="h-3 w-3" />
                              {it.photos.length} photo{it.photos.length === 1 ? '' : 's'} attached
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            <p className="mt-6 text-[10px] text-zinc-600 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Server-timestamped audit trail — read-only.
            </p>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
